import React, { useRef, useState } from 'react';
import { AssessmentResult, Scores, UserTier, investmentQuota, yieldRate } from '../types';
import { Download, RefreshCw, QrCode } from 'lucide-react';
import { DetailedSuggestions } from './DetailedSuggestions';
import { IndustryRecommendations } from './IndustryRecommendations';
import { GlassCard } from './GlassCard';
import { useNavigate } from 'react-router-dom';

interface ResultPosterProps {
  result: AssessmentResult;
  onRetake: () => void;
}

const userTier = (scores: Scores): UserTier => {
  if (scores.innovation >= 85) return 'Alpha Innovator';
  if (scores.technical >= 80) return 'Tech Architect';
  if (scores.application >= 75) return 'Early Adopter';
  return 'Explorer';
};

export function ResultPoster({ result, onRetake }: ResultPosterProps) {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isQRLoaded, setIsQRLoaded] = useState(false);
  const navigate = useNavigate();

  const scores: Scores = {
    innovation: Math.round((result.aiScore + result.web3Score) * 0.6),
    technical: Math.round(result.aiScore * 0.7 + result.web3Score * 0.3),
    application: Math.round(result.aiScore * 0.4 + result.web3Score * 0.6)
  };

  const tier = userTier(scores);

  const handleDownload = async () => {
    if (!reportRef.current) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      const ctx = finalCanvas.getContext('2d');
      
      if (ctx) {
        ctx.drawImage(canvas, 0, 0);
        ctx.font = '14px Arial';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.fillText('Generated by AIGC-DAO', 20, finalCanvas.height - 20);
        
        const link = document.createElement('a');
        link.download = `AIGC-DAO-评估报告-${new Date().toLocaleDateString()}.png`;
        link.href = finalCanvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  };

  const handleJoinClick = () => {
    // Pass the user profile data when navigating
    navigate('/product-intro', { 
      state: { 
        userProfile: result.userProfile 
      } 
    });
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div ref={reportRef} className="w-full max-w-2xl space-y-8">
        {/* 头部区域 */}
        <div className="bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl p-8 border border-[#2D3655]">
          <h2 className="text-3xl font-bold text-center bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text mb-8">
            个性化评估报告
          </h2>
          
          <div className="text-center p-6 bg-white/5 rounded-xl mb-8">
            <div className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text mb-2">
              {result.score}%
            </div>
            <div className="text-gray-300">综合得分</div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="text-center p-6 bg-[#1A1F36] rounded-xl border border-[#2D3655]">
              <div className="text-xl text-gray-200 mb-2">AI 认知水平</div>
              <div className="text-3xl font-bold text-blue-400">{result.aiScore}%</div>
              <div className="mt-2 text-sm text-gray-400">
                {result.aiScore >= 80 ? '专家级理解' :
                 result.aiScore >= 60 ? '进阶级掌握' :
                 '入门级认知'}
              </div>
            </div>
            <div className="text-center p-6 bg-[#1A1F36] rounded-xl border border-[#2D3655]">
              <div className="text-xl text-gray-200 mb-2">Web3 认知水平</div>
              <div className="text-3xl font-bold text-purple-400">{result.web3Score}%</div>
              <div className="mt-2 text-sm text-gray-400">
                {result.web3Score >= 80 ? '专家级理解' :
                 result.web3Score >= 60 ? '进阶级掌握' :
                 '入门级认知'}
              </div>
            </div>
          </div>
        </div>

        {/* 能力评估区域 */}
        <div className="bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl p-8 border border-[#2D3655]">
          <h3 className="text-xl font-semibold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            能力评估与提升建议
          </h3>
          <DetailedSuggestions result={result} />
        </div>

        {/* 行业发展区域 */}
        <div className="bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl p-8 border border-[#2D3655]">
          <h3 className="text-xl font-semibold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            行业发展路径
          </h3>
          <IndustryRecommendations userProfile={result.userProfile} />
        </div>

        {/* 结语部分 */}
        <div className="bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl p-8 border border-[#2D3655]">
          <h2 className="text-2xl font-bold text-center bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text mb-6">
            开启您的AI+Web3之旅，实现收益十倍增长
          </h2>
          
          <div className="text-center mb-8">
            <p className="text-gray-300 mb-4 leading-relaxed">
              我们专为「想通过AI行业落地、想进入Web3领域、想通过AI和Web3实现收益增长」的目标实践者设计，透过DAO社群，AI工具、稳定型Web3产品、定期的针对性实操课程、每日行业最新资讯精准进度追踪，通过AIGC-DAO的专业指导与实践，实现从认知到实践且真正能拿到结果的跨越式有目标的成长。
              让您在AI与Web3领域快速进阶，实现十倍价值增长。
            </p>
            <p className="text-gray-300 leading-relaxed">
              最好的时机就是现在！
            </p>
          </div>

          <button
            onClick={handleJoinClick}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 text-white rounded-lg transition-all transform hover:scale-105 font-semibold text-lg mb-6"
          >
            申请加入
          </button>
        </div>

        {/* 1v1咨询部分 */}
        <div className="bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl p-8 border border-[#2D3655]">
          <h2 className="text-2xl font-bold text-center bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text mb-8">
            想咨询了解更多？
          </h2>
          
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <QrCode className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white">1v1专业顾问咨询</h3>
                <p className="text-sm text-gray-400">扫码预约AI+Web3项目专家</p>
              </div>
            </div>
            
            <div className="w-48 h-48 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg overflow-hidden relative mb-4">
              {!isQRLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse" />
              )}
              
              <img 
                src="https://cdn.discordapp.com/attachments/1302237019681849355/1338466103650680832/74364141659ea182e83fb70ca47a55ad.jpg"
                alt="咨询二维码"
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  isQRLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setIsQRLoaded(true)}
                loading="eager"
                decoding="async"
              />
            </div>
            
            <p className="text-center text-sm text-gray-400">
              专家将根据您的评估结果提供针对性解决方案
            </p>
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="flex gap-4 mt-8 mb-8">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 text-white rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          保存报告
        </button>
        <button
          onClick={onRetake}
          className="flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          重新测试
        </button>
      </div>
    </div>
  );
}